// ============================================================================
// BOMB ENTITY - Dropped from aircraft
// ============================================================================

import { EntityId, Vector2 } from '@/shared/types';
import { generateId, Vec2 } from '@/shared/utils';
import { CollisionBody, ColliderType, CollisionLayers, CircleCollider } from '@/engine/CollisionSystem';

export interface BombState {
  id: EntityId;
  ownerId: EntityId;
  position: Vector2;
  velocity: Vector2;
  altitude: number;
  active: boolean;
  exploded: boolean;
  launchPosition?: Vector2; // For missiles: where they launched from
  targetPosition?: Vector2; // For missiles: where they're headed
  launchTime?: number; // When the missile was launched
  flightDuration?: number; // How long the missile takes to reach target
}

export class Bomb {
  public readonly id: EntityId;
  public readonly ownerId: EntityId; // Aircraft that dropped it
  public position: Vector2;
  public velocity: Vector2;
  public altitude: number; // Z-height (starts high, falls to 0)
  public active: boolean;
  public exploded: boolean;
  public launchPosition?: Vector2; // For missiles: starting position
  public targetPosition?: Vector2; // For missiles: target position
  public launchTime?: number; // For missiles: when launched
  public flightDuration?: number; // For missiles: how long the flight takes (ms)
  public trail: Vector2[] = []; // Trail positions for exhaust smoke
  public incomingSoundPlayed: boolean = false; // Track if incoming sound was played
  
  private readonly FALL_SPEED = 0.4; // Vertical fall velocity (800 altitude / 2000ms = 0.4)
  
  constructor(
    ownerId: EntityId,
    position: Vector2,
    horizontalVelocity: Vector2,
    altitude: number
  ) {
    this.id = generateId('bomb');
    this.ownerId = ownerId;
    this.position = { ...position };
    this.velocity = { ...horizontalVelocity };
    this.altitude = altitude;
    this.active = true;
    this.exploded = false;
  }

  update(dt: number, time: number): boolean {
    if (!this.active || this.exploded) return false;

    // Check if this is a missile (has launch data)
    const isMissile = this.launchPosition && this.targetPosition && this.launchTime !== undefined;
    
    if (isMissile) {
      // Missile with orbital trajectory
      const elapsed = time - this.launchTime!;
      
      // Don't move until launch time
      if (elapsed < 0) {
        return false;
      }
      
      const flightTime = this.flightDuration || 7000; // Use custom duration or default to 7 seconds
      const progress = Math.min(1, elapsed / flightTime);
      
      // Debug log on first movement and trigger launch sound
      if (progress > 0 && progress < 0.05 && !this.incomingSoundPlayed) {
        console.log(`[Missile] ${this.id} started moving: progress=${progress.toFixed(3)} pos=(${this.position.x.toFixed(0)},${this.position.y.toFixed(0)}) altitude=${this.altitude.toFixed(0)}`);
        this.incomingSoundPlayed = true; // Flag to play launch sound (reusing this flag)
      }
      
      // Add current position to trail (limit trail length)
      const lastTrail = this.trail[this.trail.length - 1];
      if (this.trail.length === 0 || (lastTrail && Vec2.distance(lastTrail, this.position) > 5)) {
        this.trail.push({ ...this.position });
        if (this.trail.length > 20) this.trail.shift(); // Keep last 20 positions
      }
      
      // Calculate arc trajectory (parabolic path)
      const startX = this.launchPosition!.x;
      const startY = this.launchPosition!.y;
      const endX = this.targetPosition!.x;
      const endY = this.targetPosition!.y;
      
      // Linear interpolation for X and Y
      const currentX = startX + (endX - startX) * progress;
      const currentY = startY + (endY - startY) * progress;
      
      // Arc height (parabolic curve - highest at midpoint)
      const arcHeight = 350 * Math.sin(progress * Math.PI);
      
      this.position.x = currentX;
      this.position.y = currentY;
      this.altitude = arcHeight;
      
      // Check if reached target
      if (progress >= 1) {
        this.altitude = 0;
        this.exploded = true;
        this.active = false;
        return true; // Signal explosion
      }
    } else {
      // Regular bomb from aircraft
      // Fall straight down by decreasing altitude
      this.altitude -= this.FALL_SPEED * dt;
      
      // For bombs with horizontal velocity, maintain momentum
      if (Math.abs(this.velocity.x) > 0.01 || Math.abs(this.velocity.y) > 0.01) {
        this.position.x += this.velocity.x * dt;
        this.position.y += this.velocity.y * dt;
      }
      
      // Check if hit ground (only for regular bombs, not missiles)
      if (this.altitude <= 0) {
        this.altitude = 0;
        this.exploded = true;
        this.active = false;
        return true; // Signal explosion
      }
    }

    return false;
  }

  getCollisionBody(): CollisionBody {
    const collider: CircleCollider = {
      type: ColliderType.Circle,
      offset: { x: 0, y: 0 },
      radius: 6,
    };
    
    return {
      id: this.id,
      position: this.position,
      velocity: this.velocity,
      collider,
      layer: CollisionLayers.PROJECTILE,
      mask: CollisionLayers.WALL,
      isStatic: false,
      isTrigger: true,
      userData: this,
    };
  }

  toState(): BombState {
    const state: BombState = {
      id: this.id,
      ownerId: this.ownerId,
      position: this.position,
      velocity: this.velocity,
      altitude: this.altitude,
      active: this.active,
      exploded: this.exploded,
    };
    if (this.launchPosition) state.launchPosition = this.launchPosition;
    if (this.targetPosition) state.targetPosition = this.targetPosition;
    if (this.launchTime !== undefined) state.launchTime = this.launchTime;
    if (this.flightDuration !== undefined) state.flightDuration = this.flightDuration;
    return state;
  }

  static fromState(state: BombState): Bomb {
    const bomb = new Bomb(
      state.ownerId,
      state.position,
      state.velocity,
      state.altitude
    );
    bomb.altitude = state.altitude;
    bomb.active = state.active;
    bomb.exploded = state.exploded;
    if (state.launchPosition) bomb.launchPosition = state.launchPosition;
    if (state.targetPosition) bomb.targetPosition = state.targetPosition;
    if (state.launchTime !== undefined) bomb.launchTime = state.launchTime;
    if (state.flightDuration !== undefined) bomb.flightDuration = state.flightDuration;
    return bomb;
  }
}
